import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { COMPANY } from "../data";

const ease = [0.16, 1, 0.3, 1];

const EFFECTIVE_DATE = "January 1, 2025";

export function Privacy() {
  return (
    <div data-testid="privacy-page" className="page-enter pt-32 sm:pt-40">
      <section className="container-x pb-12 max-w-4xl">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">Privacy Policy</p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance text-[#0F1E3C] mb-6"
        >
          Your privacy, respected.
        </motion.h1>
        <p className="text-base text-[#475569] leading-relaxed max-w-2xl">
          This Privacy Policy describes how Albany Staffing LLC collects, uses, shares, and
          protects information when you use our website, services, and SMS communications.
        </p>
      </section>

      <section className="container-x pb-24 max-w-4xl">
        <div className="card-soft rounded-2xl p-6 sm:p-8 mb-10 bg-white flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#FFF4EA] flex items-center justify-center text-[#E9610E] shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.15em] text-[#E9610E] font-semibold mb-1">Quick summary</p>
            <p className="text-sm text-[#334155] leading-relaxed">
              We collect only what we need to match candidates with jobs and serve our clients.
              SMS consent is never shared with third parties or affiliates for marketing. You can
              opt out of any SMS communication anytime by replying <strong>STOP</strong>.
            </p>
          </div>
        </div>

        <div className="space-y-10 text-[#334155] text-base sm:text-lg leading-relaxed">
          <Section title="1. Company Overview" number="01">
            <p>
              <strong className="text-[#0F1E3C]">Albany Staffing</strong> is a{" "}
              <strong className="text-[#0F1E3C]">DBA (Doing Business As) of Albany Systems LLC</strong>,
              a U.S.-based staffing, recruiting, and workforce solutions company providing direct
              hire, contract staffing, executive search, and related services across industries
              including Information Technology, Healthcare, Defense, Aerospace, Life Sciences, and
              Manufacturing. All references to &ldquo;Albany Staffing,&rdquo; &ldquo;we,&rdquo;
              &ldquo;our,&rdquo; or &ldquo;us&rdquo; in this policy refer to Albany Systems LLC
              operating under the Albany Staffing brand.
            </p>
          </Section>

          <Section title="2. Information We Collect" number="02">
            <p>Albany Staffing may collect the following types of information:</p>
            <ul className="list-disc list-outside ml-5 mt-3 space-y-1.5">
              <li>Full name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Resume / CV and employment history</li>
              <li>Skills, certifications, and job preferences</li>
              <li>Professional information</li>
              <li>Client company information and business contact details</li>
              <li>Information submitted through website forms, job applications, candidate
                  registration forms, or direct communication</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information" number="03">
            <p>Collected information may be used for the following purposes:</p>
            <ul className="list-disc list-outside ml-5 mt-3 space-y-1.5">
              <li>Matching candidates with suitable employment opportunities</li>
              <li>Communicating regarding job openings and career opportunities</li>
              <li>Scheduling interviews and sending interview reminders</li>
              <li>Providing application status updates</li>
              <li>Coordinating with employers and hiring managers</li>
              <li>Managing staffing and recruiting services</li>
              <li>Responding to inquiries and support requests</li>
            </ul>
          </Section>

          <Section title="4. SMS Communications & Consent" number="04">
            <p className="mb-3">
              <strong className="text-[#0F1E3C]">How you opt in:</strong> You provide express
              consent to receive SMS messages from Albany Staffing by (a) submitting your phone
              number through a website form (Contact, Job Application, Candidate Registration, or
              Request Talent), <strong>and</strong> (b) checking the clearly-labeled SMS consent
              checkbox on that form. Submitting a phone number without checking the consent box
              does <em>not</em> opt you in.
            </p>
            <p>
              <strong className="text-[#0F1E3C]">
                By providing your phone number and expressly opting in, you consent to receive
                conversational and transactional SMS messages from Albany Staffing (a DBA of
                Albany Systems LLC) related to job opportunities, interview scheduling,
                application updates, appointment reminders, and business communications.
              </strong>
            </p>
            <ul className="list-disc list-outside ml-5 mt-4 space-y-1.5">
              <li>Message and data rates may apply.</li>
              <li>Message frequency may vary based on your interactions with Albany Staffing.</li>
              <li>You can opt out of receiving SMS messages at any time by replying <strong>STOP</strong>.</li>
              <li>For assistance, reply <strong>HELP</strong> or contact Albany Staffing directly.</li>
            </ul>
            <p className="mt-4">
              For complete SMS terms, see our{" "}
              <Link to="/sms-terms" className="text-[#E9610E] underline hover:text-[#0F1E3C]">
                SMS Terms &amp; Conditions
              </Link>
              .
            </p>
          </Section>

          <Section title="5. SMS Data Sharing Statement" number="05">
            <div className="card-soft rounded-xl p-5 sm:p-6 bg-[#FFF4EA] border-[#F8D5B0]">
              <p className="text-[#0F1E3C] font-semibold">
                SMS consent is not shared with third parties or affiliates for marketing purposes.
              </p>
            </div>
          </Section>

          <Section title="6. Information Sharing & Protection" number="06">
            <ul className="list-disc list-outside ml-5 space-y-1.5">
              <li>Personal information is <strong>not sold</strong> to third parties.</li>
              <li>
                Information may be shared with potential employers or clients only for staffing
                and recruitment purposes and only as necessary to provide services.
              </li>
              <li>
                We use appropriate administrative, technical, and organizational safeguards to
                protect personal information.
              </li>
            </ul>
          </Section>

          <Section title="7. Third-Party Services" number="07">
            <p>Albany Staffing may use trusted third-party platforms for:</p>
            <ul className="list-disc list-outside ml-5 mt-3 space-y-1.5">
              <li>Website hosting</li>
              <li>Communication services</li>
              <li>Applicant tracking systems</li>
              <li>Recruiting and business operations</li>
            </ul>
            <p className="mt-3">
              These providers are required to maintain appropriate privacy and security standards.
            </p>
          </Section>

          <Section title="8. User Rights" number="08">
            <p>You may request:</p>
            <ul className="list-disc list-outside ml-5 mt-3 space-y-1.5">
              <li>Access to your personal information</li>
              <li>Corrections to inaccurate information</li>
              <li>Deletion of personal data where applicable</li>
              <li>To stop SMS communications at any time</li>
            </ul>
            <p className="mt-3">
              Requests can be sent to{" "}
              <a href={`mailto:${COMPANY.email}`} className="text-[#E9610E] underline">{COMPANY.email}</a>.
            </p>
          </Section>

          <Section title="9. Contact Information" number="09">
            <div className="card-soft rounded-2xl p-6 bg-white not-prose">
              <p className="text-[#0F1E3C] font-display font-bold text-lg">Albany Staffing</p>
              <p className="text-sm text-[#64748B] mt-0.5">A DBA of Albany Systems LLC</p>
              <p className="mt-3 text-sm"><span className="text-[#64748B]">Address:</span> {COMPANY.address}</p>
              <p className="mt-1 text-sm">
                <span className="text-[#64748B]">Email:</span>{" "}
                <a href={`mailto:${COMPANY.email}`} className="text-[#E9610E] hover:text-[#0F1E3C]">{COMPANY.email}</a>
              </p>
              <p className="mt-1 text-sm"><span className="text-[#64748B]">Phone:</span> {COMPANY.phone}</p>
            </div>
          </Section>

          <Section title="10. Effective Date" number="10">
            <p>
              This Privacy Policy is effective as of <strong className="text-[#0F1E3C]">{EFFECTIVE_DATE}</strong>.
              Albany Staffing reserves the right to update or modify this policy at any time.
              Material changes will be posted on this page with a revised effective date. Continued
              use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </Section>
        </div>

        <p className="text-xs text-[#64748B] pt-12 mt-4 border-t border-[#EAE3D2]">
          Last updated: {EFFECTIVE_DATE}
        </p>
      </section>
    </div>
  );
}

function Section({ title, number, children }) {
  return (
    <div data-testid={`privacy-section-${number}`}>
      <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#0F1E3C] mb-4">
        {title.replace(/^\d+\.\s*/, "")}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
